import { useCallback, useEffect, useRef, useState } from 'react';

import type { SxProps, Theme } from '@mui/material/styles';

/** Defines how long dismissible rows remain in their exit animation state. */
const ROW_EXIT_DURATION_MS = 200;

/** Defines the animated row height used before a dismissed row fully collapses. */
const ROW_EXIT_MAX_HEIGHT = '4.5rem';

/** Reuses one transition string so row dismissal timing stays visually consistent. */
const ROW_EXIT_TRANSITION = `opacity ${ROW_EXIT_DURATION_MS}ms ease, max-height ${ROW_EXIT_DURATION_MS}ms ease, padding-top ${ROW_EXIT_DURATION_MS}ms ease, padding-bottom ${ROW_EXIT_DURATION_MS}ms ease`;

/** Defines the optional callback that runs after a row finishes dismissing. */
export interface UseDismissibleRowsParams {
  /** Receives the dismissed username after the exit animation completes. */
  onDismissComplete?: (username: string) => void;
}

/** Returns the state and handlers needed for dismissible username-based rows. */
export interface UseDismissibleRowsResult {
  /** Tracks usernames already dismissed from the local list surface. */
  dismissedUsernames: Set<string>;
  /** Starts the exit animation for the matching row before dismissing it. */
  dismissRow: (username: string) => void;
  /** Reports whether a row is currently in its exit animation phase. */
  isRowExiting: (username: string) => boolean;
}

/** Returns the shared exit styles applied while a dismissible row animates out. */
export function getRowExitStyles(isExiting: boolean): SxProps<Theme> {
  return {
    py: isExiting ? 0 : 1,
    overflow: 'hidden',
    opacity: isExiting ? 0 : 1,
    maxHeight: isExiting ? 0 : ROW_EXIT_MAX_HEIGHT,
    transition: ROW_EXIT_TRANSITION,
  };
}

/**
 * Tracks row dismissal state so follow surfaces can share one exit-animation flow.
 *
 * The hook keeps rows visible during the exit transition, then records them as
 * dismissed so callers can filter them from future renders and optionally react
 * once the animation has completed.
 */
export function useDismissibleRows({
  onDismissComplete,
}: UseDismissibleRowsParams = {}): UseDismissibleRowsResult {
  const [dismissedUsernames, setDismissedUsernames] = useState<Set<string>>(new Set());
  const [exitingUsernames, setExitingUsernames] = useState<Set<string>>(new Set());
  const dismissalTimeoutIdsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    return () => {
      dismissalTimeoutIdsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      dismissalTimeoutIdsRef.current.clear();
    };
  }, []);

  const dismissRow = useCallback(
    (username: string) => {
      const existingTimeoutId = dismissalTimeoutIdsRef.current.get(username);

      if (existingTimeoutId !== undefined) {
        window.clearTimeout(existingTimeoutId);
      }

      setExitingUsernames((previousUsernames) => {
        const nextUsernames = new Set(previousUsernames);
        nextUsernames.add(username);
        return nextUsernames;
      });

      const timeoutId = window.setTimeout(() => {
        dismissalTimeoutIdsRef.current.delete(username);
        setDismissedUsernames((previousUsernames) => {
          const nextUsernames = new Set(previousUsernames);
          nextUsernames.add(username);
          return nextUsernames;
        });
        setExitingUsernames((previousUsernames) => {
          const nextUsernames = new Set(previousUsernames);
          nextUsernames.delete(username);
          return nextUsernames;
        });
        onDismissComplete?.(username);
      }, ROW_EXIT_DURATION_MS);

      dismissalTimeoutIdsRef.current.set(username, timeoutId);
    },
    [onDismissComplete]
  );

  /** Reports whether the named row should render exit styles. */
  const isRowExiting = useCallback(
    (username: string) => exitingUsernames.has(username),
    [exitingUsernames]
  );

  return { dismissedUsernames, dismissRow, isRowExiting };
}
